/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderDetailsViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management/dist/types';
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core';
import * as React from 'react';
import {
    IOrderDetailsProps,
    IOrderDetailsResources
} from '../../themes/dobbies/definition-extensions/order-details.ext.props.autogenerated';
import { IDobbiesOrderDetailsResources } from './dobbies-order-details.props.autogenerated';

/* VSI-Customization -- START Interface for custom component  */
interface IOrderInformationWithProps {
    orderInformation: IOrderInformation;
    resources: IOrderDetailsResources;
}

interface IContextProps {
    context: ICoreContext;
    resources: IOrderDetailsResources;
}

interface IOrderDetailHelpSlot {
    slots: {
        textBlock: React.ReactNode[];
    };
}

interface ISalesOrderHeaderInfo {
    salesOrderId: string | undefined;
    salesOrderTotalAmount: React.ReactNode;
    context?: ICoreContext;
}

interface ISummaryLineProps {
    name: string;
    label: string;
    value?: string;
}

interface DeliveryAddressShip {
    props: {
        deliveryType: string;
        address: {
            Name: string;
            Street: string;
            City: string;
            ZipCode: string;
            Phone: string;
        };
    };
}

/* VSI-Customization -- END  */

export const OrderDetailsOrderInfomationHeading: React.FC<IOrderInformation> = ({ channelName }) => (
    <Node tag='div' className='heading'>
        {channelName}
    </Node>
);

export const OrderDetailsGroupDeliveryGuest = (props: IOrderDetailsViewProps) => {
    const delivery_ship = props.groups?.groups.find(addr => {
        const deliveryAddress = addr.delivery.address as DeliveryAddressShip | undefined;
        const deliveryType = deliveryAddress?.props?.deliveryType;
        return deliveryType && deliveryType.trim().toLowerCase() === 'ship';
    });
    return (
        <div className='row'>
            {[delivery_ship]?.length ? (
                [delivery_ship]?.map((addr, index) => {
                    const deliveryAddressShip = addr?.delivery.address as DeliveryAddressShip | undefined;
                    if (addr && deliveryAddressShip?.props?.deliveryType === 'ship') {
                        const address_name = deliveryAddressShip?.props?.address.Name;
                        const address_street = deliveryAddressShip?.props?.address.Street;
                        const address_city = deliveryAddressShip?.props?.address.City;
                        const address_zipcode = deliveryAddressShip?.props?.address.ZipCode;
                        let address_phone = deliveryAddressShip?.props?.address.Phone;
                        if (!address_phone) {
                            const phoneNumObj = props.data.orderHydration.result?.salesOrder.ContactInformationCollection?.find(
                                ci => ci.ContactInformationTypeValue === 1
                            );
                            if (phoneNumObj && phoneNumObj.Value) {
                                address_phone = phoneNumObj.Value;
                            }
                        }
                        return (
                            <div className='col-12 col-md-6' key={index}>
                                <div className='order-delivery__wrap'>
                                    <h5>Home delivery address:</h5>
                                    <p>
                                        {address_name}
                                        <br />
                                        {address_street} {address_city} {address_zipcode}
                                        <br />
                                        Phone: {address_phone} <br />
                                        Email address: {props.salesOrder?.ReceiptEmail ? props.salesOrder?.ReceiptEmail : ''}
                                    </p>
                                </div>
                            </div>
                        );
                    } else {
                        return <></>;
                    }
                })
            ) : (
                <></>
            )}
        </div>
    );
};

const _displayHeadingRow = (resources: IOrderDetailsResources | IDobbiesOrderDetailsResources) => {
    const { deliveryHeading, dispatchedByLabel, dobbiesLabel } = resources;
    return (
        <Node tag='div' className='ms-heading row delivery-dispatched-details'>
            <Node tag='div' className='delivery-dispatched-details__count'>
                <Node tag='span' className='__heading'>
                    {deliveryHeading}{' '}
                </Node>
            </Node>
            <Node tag='div' className='delivery-dispatched-details__right'>
                <Node tag='div' className='dispatched-by-vendor'>
                    <Node tag='span' className='label'>
                        {dispatchedByLabel}{' '}
                    </Node>
                    <Node tag='span' className='vendor-name'>
                        {dobbiesLabel}
                    </Node>
                </Node>
            </Node>
        </Node>
    );
};

export const OrderDetailsOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelAddress
}) => (
    <Node {...orderInformationProps}>
        {createdDate}
        {count}
        {amount}
        {channelAddress}
        {channelReferenceId}
        {salesId}
        {receiptId}
    </Node>
);

/* VSI-Customization -- START Created Functional Component  */
export const OrderDetailsOrderInformationWithLabels: React.FC<IOrderInformationWithProps & ISalesOrderHeaderInfo> = props => {
    const { orderInformationProps, receiptId, createdDate, count, channelReferenceId, channelAddress } = props.orderInformation;
    const {
        resources: { orderPlaceLabel, orderLabel, orderIdLabel, salesIdInProcessText },
        salesOrderId,
        salesOrderTotalAmount
    } = props;
    const isAuthenticated = props['context'] ? props['context'].request?.user.isAuthenticated : false;
    const orderNo = (
        <Node tag='div' className='prior-info'>
            <Node className='order-information-sales-id' tag='span'>
                <Node tag='label' className=''>
                    {orderIdLabel}
                </Node>
                <Node tag='span' className='ms-order-details__order-information-number'>
                    {salesOrderId || salesIdInProcessText}
                </Node>
            </Node>
        </Node>
    );

    return isAuthenticated ? (
        <Node {...orderInformationProps}>
            <Node tag='div' className='row'>
                <Node tag='div' className='col-12'>
                    <Node tag='div' className='prior-info d-md-flex'>
                        <span className='prior-info__order-placed'>
                            <Node tag='label' className=''>
                                {orderPlaceLabel}
                            </Node>
                            {createdDate}
                        </span>

                        <div className='prior-info__count-total'>
                            <Node tag='span' className='prior-info__order-count'>
                                {count}
                            </Node>
                            <Node tag='span' className='prior-info__order-total'>
                                <Node tag='label' className=''>
                                    {orderLabel}
                                </Node>
                                {salesOrderTotalAmount}
                            </Node>
                        </div>
                    </Node>
                </Node>
                <Node tag='div' className='col-12'>
                    <div className='ms-order-details__order-information-channel-address'>
                        {/* @ts-ignore */}
                        {channelAddress?.props?.children.toString().slice(0, -3)}
                    </div>
                    {channelReferenceId}
                    {orderNo}
                    {receiptId}
                </Node>
            </Node>
        </Node>
    ) : (
        <Node {...orderInformationProps}>
            <Node tag='div' className='row'>
                <Node tag='div' className='col-12'>
                    {orderNo}
                    <Node tag='div' className='prior-info d-flex'>
                        <span className='prior-info__order-placed'>
                            <Node tag='label' className=''>
                                {orderPlaceLabel}
                            </Node>
                            {createdDate}
                        </span>
                        <div className='prior-info__count-total'>
                            <Node tag='span' className='prior-info__order-count'>
                                {count}
                            </Node>
                            <Node tag='span' className='prior-info__order-total'>
                                <Node tag='label' className=''>
                                    {orderLabel}
                                </Node>
                                {salesOrderTotalAmount}
                            </Node>
                        </div>
                    </Node>
                </Node>
                <Node tag='div' className='col-12'>
                    <div className='ms-order-details__order-information-channel-address'>
                        {/* @ts-ignore */}
                        {channelAddress?.props?.children.toString().slice(0, -3)}
                    </div>
                    {channelReferenceId}

                    {receiptId}
                </Node>
            </Node>
        </Node>
    );
};
/* VSI-Customization -- END  */
export const OrderDetailsSalesLine: React.FC<ISalesLine & IContextProps> = props => {
    const { salesLineProps, salesLine, buyAgainButton, context } = props;
    const { isAuthenticated } = context.request.user;
    return (
        <Node {...salesLineProps}>
            {salesLine}
            {isAuthenticated && buyAgainButton}
        </Node>
    );
};

export const OrderDetailsGroupDelivery: React.FC<IGroupDelivery & IContextProps> = ({
    deliveryProps,
    heading,
    count,
    trackingInfo,
    shippedStatusCheck
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {/* {false && shippedStatusCheck === 'processing' ? trackingInfo : null} */}
    </Node>
);

export const OrderDetailsGroup: React.FC<IGroup & IContextProps> = ({
    groupProps,
    delivery,
    address,
    salesLinesProps,
    salesLines,
    context,
    resources,
    data
}) => {
    const { isAuthenticated } = context.request.user;
    let addressLabel, addressValue: string;
    if (data.deliveryType.toLowerCase() === 'ship') {
        addressLabel = resources.deliveryAddressLabel;
        // @ts-ignore
        const fullAddress = address ? address.props.address.FullAddress : '';
        addressValue = fullAddress.slice(0, fullAddress.length - 4);
    } else {
        if (
            data.deliveryType.toLowerCase() === 'clickcol' ||
            data.deliveryType.toLowerCase() === 'pickup' ||
            data.deliveryType.toLowerCase() === 'click and collect' ||
            data.deliveryType.toLowerCase() === 'click & collect'
        ) {
            addressLabel = resources.storeAddressLabel;
            // @ts-ignore
            addressValue = address ? address.props.address.Name : '';
        } else {
            addressLabel = '';
            // @ts-ignore
            addressValue = '';
        }
    }
    return (
        <Node {...groupProps}>
            {delivery && <OrderDetailsGroupDelivery resources={resources} context={context} {...delivery} />}
            {salesLines && (
                <Node {...salesLinesProps}>
                    <Node tag='div' className='prior-info'>
                        <Node tag='div' className='prior-info__delivery-address'>
                            {addressLabel ? (
                                <Node tag='label' className=''>
                                    {addressLabel}
                                </Node>
                            ) : (
                                <></>
                            )}
                            {addressValue ? (
                                <Node tag='span' className=''>
                                    {addressValue}
                                </Node>
                            ) : (
                                <></>
                            )}
                        </Node>
                    </Node>
                    {salesLines.map(salesLine => {
                        const deliveryType = salesLine.data.deliveryType;
                        const salesStatus = salesLine.data.salesStatus;
                        let groupSalesStatus = `${salesLine.data.salesStatus}`;
                        // Capitalizing first letter of status
                        groupSalesStatus = groupSalesStatus.charAt(0).toUpperCase() + groupSalesStatus.slice(1);
                        if (salesLine.data.deliveryType.toLowerCase() === 'carryout') {
                            groupSalesStatus = 'Completed';
                        } else if (salesStatus.toLowerCase() === 'canceled') {
                            groupSalesStatus = 'Cancelled';
                        } else if (salesStatus.toLowerCase() === 'pickedup') {
                            if (deliveryType.toLowerCase() === 'ship') {
                                groupSalesStatus = 'Shipped';
                            } else if (deliveryType.toLowerCase() === 'pickup') {
                                groupSalesStatus = 'Picked Up';
                            }
                        } else if (salesStatus === 'readyForPickup') {
                            if (deliveryType.toLowerCase() === 'ship') {
                                groupSalesStatus = 'Preparing for dispatch';
                            } else if (deliveryType.toLowerCase() === 'pickup') {
                                groupSalesStatus = 'Ready For Pickup';
                            }
                        }

                        return (
                            <>
                                {salesStatus.toLowerCase() !== 'canceled' ? (
                                    <React.Fragment key={salesLine.data.salesLine.LineId}>
                                        {/* {false && salesLine.data.deliveryType === 'ship' && salesLine.data.shipment ? salesLine.trackingInfo : null} */}
                                        <OrderDetailsSalesLine context={context} resources={resources} {...salesLine} />
                                        {isAuthenticated && (
                                            <Node tag='span' className='ms-order-details-status'>
                                                <Node tag='span' className=''>
                                                    {resources.orderStatusLabel}{' '}
                                                </Node>
                                                <Node tag='span' className='ms-order-details__sales-status'>
                                                    {groupSalesStatus}
                                                </Node>
                                            </Node>
                                        )}
                                    </React.Fragment>
                                ) : (
                                    <></>
                                )}
                            </>
                        );
                    })}
                </Node>
            )}
            {address}
        </Node>
    );
};

export const OrderDetailsGroups: React.FC<IGroups & IContextProps> = ({ groupsProps, groups, context, resources }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderDetailsGroup context={context} resources={resources} key={index} {...group} />
        ))}
    </Node>
);

export const OrderDetailsGroupGuest: React.FC<IGroup & IContextProps> = ({
    groupProps,
    delivery,
    address,
    salesLinesProps,
    salesLines,
    context,
    resources,
    data
}) => {
    const deliveryAddress = address as { props: { deliveryType: string; address: { FullAddress: string } } } | undefined;
    return (
        <Node {...groupProps}>
            {salesLines && (
                <Node {...salesLinesProps}>
                    {deliveryAddress?.props?.deliveryType === 'Click and Collect' && (
                        <Node tag='div' className='prior-info'>
                            <Node tag='div' className='prior-info__delivery-address'>
                                <Node tag='label' className=''>
                                    {'Collection store:'}
                                </Node>
                                <Node tag='span' className=''>
                                    {deliveryAddress?.props?.address ? deliveryAddress?.props?.address.FullAddress : ''}
                                </Node>
                            </Node>
                        </Node>
                    )}
                    {salesLines.map(salesLine => {
                        return (
                            <React.Fragment key={salesLine.data.salesLine.LineId}>
                                <OrderDetailsSalesLine context={context} resources={resources} {...salesLine} />
                            </React.Fragment>
                        );
                    })}
                </Node>
            )}
        </Node>
    );
};

export const OrderDetailsGroupsGuest: React.FC<IGroups & IContextProps> = ({ groupsProps, groups, context, resources }) => {
    // filter deliver groups
    const filteredDeliveryGroups = groups.filter(group => {
        const deliveryType = group.data.deliveryType.trim().toLowerCase();
        return deliveryType === 'ship';
    });
    // filter pickedup groups
    const filteredPickedupGroups = groups.filter(group => {
        const deliveryType = group.data.deliveryType.trim().toLowerCase();
        return deliveryType === 'click and collect' || deliveryType === 'pickup' || deliveryType !== 'ship';
    });
    return (
        <>
            {filteredDeliveryGroups.length > 0 && _displayHeadingRow(resources)}
            <Node {...groupsProps}>
                {filteredDeliveryGroups.map((group, index) => (
                    <OrderDetailsGroupGuest context={context} resources={resources} key={index} {...group} />
                ))}
                {filteredPickedupGroups.map((group, index) => (
                    <>
                        <Node tag='div' className='delivery-dispatched-details__count'>
                            <Node tag='span' className='__heading'>
                                {`Click & collect | Order ${index + 1}`}{' '}
                            </Node>
                        </Node>
                        <OrderDetailsGroupGuest context={context} resources={resources} key={index} {...group} />
                    </>
                ))}
            </Node>
        </>
    );
};

export const OrderDetailsOrderSummary: React.FC<IOrderSummary & { shippingLabel: string; shippingAmount: string }> = props => {
    const { orderSummaryProps, heading, subtotal, totalAmount, earnedPoints, shippingLabel, shippingAmount } = props;
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {/* {shipping} */}
            <OrderSummaryLine name='home-delivery' label={shippingLabel} value={shippingAmount} />
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};

export const OrderDetailsPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderDetailsHelp: React.FC<IHelp & IOrderDetailHelpSlot> = ({
    helpProps,
    needHelpLabel,
    helpLineNumberLabel,
    contactNumber,
    slots
}) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
        {slots.textBlock && slots.textBlock.length > 0 && slots.textBlock[0]}
    </Node>
);

/* VSI Customization - START - 27/01/21 */
const OrderSummaryLine: React.FC<ISummaryLineProps> = ({ name, label, value }) => (
    <Node tag='p' className={`ms-order-details__order-summary-line-${name}`}>
        <Node tag='span' className={`ms-order-details__order-summary-label`}>
            {label}
        </Node>
        <Node tag='span' className={`ms-order-details__order-summary-price`}>
            {value}
        </Node>
    </Node>
);
/*  This function is used to display Order summary when order is cancelled
    As curretcly, Free label is displayed but we'll display £0.00 instead of Free */
const _getZeroPriceInOrderSummary = (
    zeroAmount: string,
    orderSummaryProps: INodeProps,
    heading: React.ReactNode,
    earnedPoints: React.ReactNode | undefined,
    canShip: boolean | undefined,
    resources: IOrderDetailsResources
) => {
    const { orderSummaryGrandTotalLabel, orderSummaryItemsTotalLabel, orderSummaryShippingFeeLabel, orderSummaryTaxLabel } = resources;
    return (
        <Node {...orderSummaryProps}>
            {heading}
            <OrderSummaryLine name='subtotal' label={orderSummaryItemsTotalLabel} value={zeroAmount} />
            {canShip && <OrderSummaryLine name='shipping' label={orderSummaryShippingFeeLabel} value={zeroAmount} />}
            <OrderSummaryLine name='tax-amount' label={orderSummaryTaxLabel} value={zeroAmount} />
            <OrderSummaryLine name='total-amount' label={orderSummaryGrandTotalLabel} value={zeroAmount} />
            {earnedPoints}
        </Node>
    );
};
// Format any given number to currency
const _formatCurrency = (price: number | undefined, context: ICoreContext): string => {
    // If price is undefined, suppose it to be zero
    return context.cultureFormatter.formatCurrency(price ? price : 0, context.request.channel?.Currency);
};
/* VSI CUstomization - END */

const OrderDetailsView: React.FC<IOrderDetailsViewProps & IOrderDetailsProps<{}>> = props => {
    const {
        moduleProps,
        heading,
        alert,
        loading,
        orderInfomation,
        tableViewActions,
        orderSummary,
        payment,
        help,
        groups,
        resources,
        context,
        slots,
        salesOrder
    } = props;
    /* VSI-Customization -- START If user is not authenticated then redirets  */
    // if (!context.request.user.isAuthenticated) {
    //     const signinURL = props.context.request.user.signInUrl;
    //     window.location.assign(signinURL!);
    // }
    const { isAuthenticated } = context.request.user;
    console.log('res', resources.orderItemLabel);

    //    const taxedShippingAmount = (salesOrder?.TotalAmount || 0) - (salesOrder?.SubtotalAmount || 0);
    const taxedShippingAmount = (salesOrder?.ShippingChargeAmount || 0) + (salesOrder?.TaxOnShippingCharge || 0);

    const salesOrderId = salesOrder && salesOrder.SalesId;
    const totalAmount = salesOrder && salesOrder.TotalAmount;
    const salesOrderTotal = _formatCurrency(totalAmount, context);
    // Check if total amount is 0, £0.00 will be dislayed instead of 'Free' label otherwise display amount as received in view props
    const salesOrderAmount =
        totalAmount && totalAmount !== 0 ? (
            orderInfomation && orderInfomation.amount
        ) : (
            <Node className={'msc-price ms-order-details__order-information-amount'} tag='span'>
                {salesOrderTotal}
            </Node>
        );
    const salesOrderHeaderInfo: ISalesOrderHeaderInfo = {
        salesOrderId: salesOrderId,
        salesOrderTotalAmount: <>{salesOrderAmount}</>
    };
    // Check if it is shipping order or pickup when we need to display zero amount of shipping
    const pickupDeliveryModeCode = context.request && context.request.channel && context.request.channel.PickupDeliveryModeCode;
    const canShip =
        salesOrder &&
        salesOrder.SalesLines &&
        salesOrder.SalesLines.some(orderDetailsLine =>
            orderDetailsLine.DeliveryMode && orderDetailsLine.DeliveryMode !== ''
                ? orderDetailsLine.DeliveryMode !== pickupDeliveryModeCode
                : orderDetailsLine
        );
    /* VSI-Customization -- END  */
    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {isAuthenticated ? (
                <>
                    {orderInfomation && <OrderDetailsOrderInfomationHeading {...orderInfomation} {...props} />}

                    <Node tag='div' className='row'>
                        <Node tag='div' className='col-lg-7 ms-order-details__products'>
                            {orderInfomation && (
                                <OrderDetailsOrderInformationWithLabels
                                    orderInformation={orderInfomation}
                                    resources={resources}
                                    {...salesOrderHeaderInfo}
                                    {...context}
                                />
                            )}
                            {groups && <OrderDetailsGroups context={context} resources={resources} {...groups} />}
                            <Node tag='div' className='order-details-btns-group'>
                                {tableViewActions}
                                <a href='/orderhistory' aria-label='' className='ms-order-history__btn-order-details msc-btn'>
                                    Back to order history
                                </a>
                            </Node>
                        </Node>
                        <Node tag='div' className='col-lg-5'>
                            <Node tag='div' className='row'>
                                <Node tag='div' className='col-lg-12'>
                                    {orderSummary &&
                                        (totalAmount && totalAmount !== 0 ? (
                                            <OrderDetailsOrderSummary
                                                {...orderSummary}
                                                shippingLabel={resources.orderSummaryShippingFeeLabel}
                                                shippingAmount={_formatCurrency(taxedShippingAmount, context)}
                                            />
                                        ) : (
                                            _getZeroPriceInOrderSummary(
                                                salesOrderTotal,
                                                orderSummary.orderSummaryProps,
                                                orderSummary.heading,
                                                orderSummary.earnedPoints,
                                                canShip,
                                                resources
                                            )
                                        ))}
                                    {payment && <OrderDetailsPayment {...payment} />}
                                </Node>
                                <Node tag='div' className='col-lg-12 col-md-6 col-sm-12'>
                                    {help && <OrderDetailsHelp slots={slots} {...help} />}
                                </Node>
                            </Node>
                        </Node>
                    </Node>
                </>
            ) : (
                <>
                    <Node tag='div' className='row'>
                        <Node
                            tag='div'
                            className={`col-lg-8 ms-order-details__products ${
                                isAuthenticated ? 'signin-order-detail' : 'signout-order-detail'
                            }`}
                        >
                            {orderInfomation && <OrderDetailsOrderInfomationHeading {...orderInfomation} {...props} />}
                            {orderInfomation && (
                                <OrderDetailsOrderInformationWithLabels
                                    orderInformation={orderInfomation}
                                    resources={resources}
                                    {...salesOrderHeaderInfo}
                                    {...context}
                                />
                            )}
                            {!isAuthenticated && <OrderDetailsGroupDeliveryGuest {...props} />}
                            {groups && <OrderDetailsGroupsGuest context={context} resources={resources} {...groups} />}
                        </Node>
                        <Node tag='div' className='col-lg-4'>
                            <Node tag='div' className='row'>
                                <div className='col-lg-12'>
                                    {orderSummary &&
                                        (totalAmount && totalAmount !== 0 ? (
                                            <OrderDetailsOrderSummary
                                                {...orderSummary}
                                                shippingLabel={resources.orderSummaryShippingFeeLabel}
                                                shippingAmount={_formatCurrency(taxedShippingAmount, context)}
                                            />
                                        ) : (
                                            _getZeroPriceInOrderSummary(
                                                salesOrderTotal,
                                                orderSummary.orderSummaryProps,
                                                orderSummary.heading,
                                                orderSummary.earnedPoints,
                                                canShip,
                                                resources
                                            )
                                        ))}
                                    {payment && <OrderDetailsPayment {...payment} />}
                                </div>
                                <div className='col-lg-12'>
                                    <Node className='help-centre-block' tag='div'>
                                        {slots.helpCentreBlock}
                                    </Node>
                                    <Node className='credit-availability-banner' tag='div'>
                                        {slots.textBlockBanner}
                                    </Node>
                                    {help && <OrderDetailsHelp slots={slots} {...help} />}
                                </div>
                            </Node>
                        </Node>
                    </Node>
                </>
            )}
        </Module>
    );
};

export default OrderDetailsView;
